<template>
  <div class="product-grid-toolbar">
    <div class="toolbar-row toolbar-row--primary">
      <grid-col-manage :columnDefs="columnDefs" :hiden_columns="hiden_columns" @toggle_column="$emit('toggle_column', $event)" />
      <h2 class="toolbar-title">Katalogu i produkteve</h2>
      <div class="global-search">
        <span class="global-search__icon"><i class="fa fa-search"></i></span>
        <input
          class="input global-search__input"
          type="text"
          :value="search"
          placeholder="Kërko sipas kodit, emrit ose barkodit"
          autocomplete="off"
          @input="onSearchInput"
          @keydown="onSearchKeydown"
          @blur="onSearchBlur"
          @focus="onSearchFocus"
        />
        <button v-if="search" type="button" class="global-search__clear" title="Pastro kërkimin" @click="clearSearch">
          <i class="fa fa-times"></i>
        </button>
        <ul v-if="showSuggestions" class="global-search__suggest" role="listbox">
          <li v-if="suggesting" class="global-search__suggest-status">Duke kërkuar...</li>
          <li
            v-for="(item, index) in suggestions"
            :key="item.guid || item.code || index"
            class="global-search__suggest-item"
            :class="{ 'global-search__suggest-item--active': index === activeIndex }"
            role="option"
            @mousedown.prevent="selectSuggestion(item)"
            @mouseenter="activeIndex = index"
          >
            <span class="global-search__suggest-code">{{ item.code }}</span>
            <span class="global-search__suggest-name">{{ item.name }}</span>
            <span v-if="item.status === 0" class="global-search__suggest-note">jo aktiv</span>
          </li>
          <li v-if="!suggesting && suggestions.length === 0 && suggestTouched" class="global-search__suggest-status">Nuk u gjet asnjë produkt</li>
        </ul>
      </div>
      <span v-if="loading" class="toolbar-loading"><i class="fa fa-spinner fa-spin"></i> Duke filtruar...</span>
    </div>

    <div class="toolbar-row toolbar-row--filters">
      <div class="quick-filters">
        <span class="quick-filters__label">Status:</span>
        <button
          v-for="opt in statusQuickOptions"
          :key="'status-' + opt.value"
          type="button"
          class="chip"
          :class="{ 'chip--active': filters.status === opt.value }"
          @click="$emit('quick-filter', { field: 'status', value: opt.value })"
        >
          {{ opt.label }}
        </button>
      </div>
      <div class="quick-filters">
        <span class="quick-filters__label">Tipi:</span>
        <button
          v-for="opt in accessoryQuickOptions"
          :key="'acc-' + opt.value"
          type="button"
          class="chip"
          :class="{ 'chip--active': filters.is_accessory === opt.value }"
          @click="$emit('quick-filter', { field: 'is_accessory', value: opt.value })"
        >
          {{ opt.label }}
        </button>
      </div>
      <div class="quick-filters">
        <span class="quick-filters__label">Filtra:</span>
        <button type="button" class="chip" :class="{ 'chip--active': filters.on_offer === '1' }" @click="toggleOfferFilter">Trego vetëm produktet me ofertë aktuale</button>
        <button type="button" class="chip" :class="{ 'chip--active': filters.new_codes === '1' }" @click="toggleNewCodesFilter">Trego Kodet e Reja</button>
        <template v-if="filters.new_codes === '1'">
          <label class="date-range">
            <span>Nga</span>
            <input type="date" class="input is-small date-range__input" :value="filters.created_from || ''" @change="onDateRangeChange('created_from', $event.target.value)" />
          </label>
          <label class="date-range">
            <span>Deri</span>
            <input type="date" class="input is-small date-range__input" :value="filters.created_to || ''" @change="onDateRangeChange('created_to', $event.target.value)" />
          </label>
        </template>
        <button type="button" class="chip" :class="{ 'chip--active': filters.edited_codes === '1' }" @click="toggleEditedCodesFilter">Trego Kodet e Edituara</button>
        <template v-if="filters.edited_codes === '1'">
          <label class="date-range">
            <span>Nga</span>
            <input type="date" class="input is-small date-range__input" :value="filters.updated_from || ''" @change="onDateRangeChange('updated_from', $event.target.value)" />
          </label>
          <label class="date-range">
            <span>Deri</span>
            <input type="date" class="input is-small date-range__input" :value="filters.updated_to || ''" @change="onDateRangeChange('updated_to', $event.target.value)" />
          </label>
        </template>
      </div>
      <div class="quick-filters">
        <span class="quick-filters__label">Stoku F5:</span>
        <button
          v-for="opt in stockQuickOptions"
          :key="'stock-' + opt.value"
          type="button"
          class="chip"
          :class="{ 'chip--active': (filters.has_stock || '') === opt.value }"
          @click="$emit('quick-filter', { field: 'has_stock', value: opt.value })"
        >
          {{ opt.label }}
        </button>
      </div>
      <div class="toolbar-spacer"></div>
      <label class="page-size">
        <span>Faqe:</span>
        <select :value="perPage" @change="$emit('update:perPage', Number($event.target.value))">
          <option v-for="size in pageSizes" :key="size" :value="size">{{ size }}</option>
        </select>
      </label>
      <div class="toolbar-actions">
        <button type="button" class="toolbar-action toolbar-action--import" title="Importo etiketa nga skedar" @click="$emit('import-labels')">
          <i class="fa fa-tags"></i>
          <small>Importo kodet per Etikete</small>
        </button>
        <button
          type="button"
          class="toolbar-action toolbar-action--import"
          title="Importo kode për selektim dhe ndryshim në grup"
          @click="$emit('import-bulk-codes')"
        >
          <i class="fa fa-edit"></i>
          <small>Importo kode për Ndryshim</small>
        </button>
        <button
          type="button"
          class="toolbar-action toolbar-action--import"
          title="Eksporto të gjitha produktet që përputhen me filtrat aktualë (pa selektim)"
          :disabled="gridExportLoading || !totalRows"
          @click="$emit('export-grid')"
        >
          <i class="fa fa-file-excel" :class="{ 'fa-spinner fa-spin': gridExportLoading }"></i>
          <small>Eksporto të filtruarat</small>
        </button>
        <button type="button" class="toolbar-action toolbar-action--import" title="Eksporto produktet me atribute" :disabled="attributesExportLoading" @click="$emit('export-attributes')">
          <i class="fa fa-file-csv" :class="{ 'fa-spinner fa-spin': attributesExportLoading }"></i>
          <small>Eksporto atribute</small>
        </button>
        <a @click.prevent="$emit('open-services')" class="toolbar-action"><small>Sherbime</small></a>
        <a @click.prevent="$emit('open-brands')" class="toolbar-action"><small>Marka</small></a>
        <div class="dropdown is-right is-hoverable">
          <div class="dropdown-trigger">
            <button type="button" class="button is-small is-light">
              <span>Më shumë</span>
              <span class="icon is-small"><i class="fa fa-angle-down"></i></span>
            </button>
          </div>
          <div class="dropdown-menu" role="menu">
            <div class="dropdown-content">
              <a @click.prevent="$emit('online-health')" class="dropdown-item"> <i class="mr-2 fa fa-heartbeat"></i> Sinkronizimi online </a>
              <a @click.prevent="$emit('system-health')" class="dropdown-item"> <i class="mr-2 fa fa-stethoscope"></i> Sinkronizimi sistemit </a>
            </div>
          </div>
        </div>
        <a v-if="canCreate" @click.prevent="$emit('create')" class="toolbar-action toolbar-action--create" title="Krijo produkt">
          <i class="fa fa-plus"></i>
        </a>
      </div>
    </div>

    <div v-if="activeChips.length" class="toolbar-row toolbar-row--chips">
      <span v-for="chip in activeChips" :key="chip.key" class="filter-chip">
        {{ chip.label }}
        <button type="button" class="filter-chip__remove" @click="$emit('remove-filter', chip.key)">
          <i class="fa fa-times"></i>
        </button>
      </span>
      <button type="button" class="filter-chip filter-chip--clear" @click="$emit('clear-filters')">Pastro të gjitha filtrat</button>
    </div>

    <div v-if="totalRows > 0" class="toolbar-row toolbar-row--summary">
      <small class="result-summary">{{ resultSummary }}</small>
    </div>
  </div>
</template>

<script>
import gridColManage from "@/components/grid_col_manage.vue";
import ProductsServices from "@/services/Product";

export default {
  components: { gridColManage },
  props: {
    columnDefs: { type: Array, default: () => [] },
    hiden_columns: { type: Array, default: () => [] },
    search: { type: String, default: "" },
    filters: { type: Object, default: () => ({}) },
    perPage: { type: Number, default: 50 },
    loading: { type: Boolean, default: false },
    totalRows: { type: Number, default: 0 },
    currentPage: { type: Number, default: 1 },
    columnLabels: { type: Object, default: () => ({}) },
    pinnedCodes: { type: Array, default: () => [] },
    canCreate: { type: Boolean, default: false },
    attributesExportLoading: { type: Boolean, default: false },
    gridExportLoading: { type: Boolean, default: false },
  },
  emits: [
    "toggle_column",
    "update:search",
    "update:perPage",
    "quick-filter",
    "toggle-offer",
    "toggle-new-codes",
    "toggle-edited-codes",
    "date-range",
    "remove-filter",
    "clear-filters",
    "pin-search",
    "select-suggestion",
    "open-services",
    "open-brands",
    "online-health",
    "system-health",
    "import-labels",
    "import-bulk-codes",
    "export-grid",
    "export-attributes",
    "create",
  ],
  data() {
    return {
      pageSizes: [25, 50, 100],
      statusQuickOptions: [
        { value: "", label: "Të gjitha" },
        { value: "1", label: "Aktiv" },
        { value: "0", label: "Jo aktiv" },
      ],
      accessoryQuickOptions: [
        { value: "", label: "Të gjitha" },
        { value: "0", label: "Produkt" },
        { value: "1", label: "Aksesor" },
      ],
      stockQuickOptions: [
        { value: "", label: "Të gjitha" },
        { value: "1", label: "Me stok" },
        { value: "0", label: "Pa stok" },
      ],
      suggestions: [],
      suggesting: false,
      suggestTouched: false,
      suggestOpen: false,
      activeIndex: -1,
      suggestTimer: null,
      suggestRequestId: 0,
    };
  },
  computed: {
    showSuggestions() {
      return this.suggestOpen && (this.suggesting || this.suggestions.length > 0 || this.suggestTouched);
    },
    activeChips() {
      const chips = [];
      (this.pinnedCodes || []).forEach((code) => {
        chips.push({ key: `code:${code}`, label: `Kodi: ${code}` });
      });
      if (this.search?.trim() && !this.pinnedCodes?.length) {
        chips.push({ key: "__search__", label: `Kërkim: ${this.search.trim()}` });
      }
      Object.entries(this.filters || {}).forEach(([field, value]) => {
        if (value === null || value === undefined || value === "") return;
        if (Array.isArray(value) && value.length === 0) return;
        if (this.pinnedCodes?.length && (field === "code" || field === "codes")) return;
        if (field === "created_from" || field === "created_to" || field === "updated_from" || field === "updated_to") return;
        if (field === "on_offer" && value === "1") {
          chips.push({ key: "on_offer", label: "Ka ofertë aktuale" });
          return;
        }
        if (field === "has_offer" && value === "1") {
          chips.push({ key: "has_offer", label: "Ka ofertë aktuale" });
          return;
        }
        if (field === "new_codes" && value === "1") {
          const from = this.formatChipDate(this.filters.created_from);
          const to = this.formatChipDate(this.filters.created_to);
          const range = from && to ? `${from} – ${to}` : from || to || "";
          chips.push({ key: "new_codes", label: range ? `Kodet e reja: ${range}` : "Kodet e reja" });
          return;
        }
        if (field === "edited_codes" && value === "1") {
          const from = this.formatChipDate(this.filters.updated_from);
          const to = this.formatChipDate(this.filters.updated_to);
          const range = from && to ? `${from} – ${to}` : from || to || "";
          chips.push({ key: "edited_codes", label: range ? `Kodet e edituara: ${range}` : "Kodet e edituara" });
          return;
        }
        if (field === "has_stock") {
          chips.push({
            key: "has_stock",
            label: value === "1" ? "Stoku F5: Me stok" : value === "0" ? "Stoku F5: Pa stok" : `Stoku F5: ${value}`,
          });
          return;
        }
        const label = this.columnLabels[field] || field;
        const display = this.formatFilterValue(field, value);
        chips.push({ key: field, label: `${label}: ${display}` });
      });
      return chips;
    },
    resultSummary() {
      const start = (this.currentPage - 1) * this.perPage + 1;
      const end = Math.min(this.currentPage * this.perPage, this.totalRows);
      if (this.totalRows === 0) return "Nuk u gjet asnjë produkt";
      const pinNote = this.pinnedCodes?.length > 1 ? ` (${this.pinnedCodes.length} kode të fiksuara)` : this.pinnedCodes?.length === 1 ? " (1 kod i fiksuar)" : "";
      return `Duke shfaqur ${start.toLocaleString()}–${end.toLocaleString()} nga ${this.totalRows.toLocaleString()} produkte${pinNote}`;
    },
  },
  watch: {
    search(val) {
      if (!String(val || "").trim()) this.clearSuggestions();
    },
  },
  beforeUnmount() {
    if (this.suggestTimer) clearTimeout(this.suggestTimer);
  },
  methods: {
    onSearchInput(event) {
      const value = event.target.value;
      this.$emit("update:search", value);
      this.queueSuggest(value);
    },
    queueSuggest(value) {
      if (this.suggestTimer) clearTimeout(this.suggestTimer);
      const q = String(value || "").trim();
      if (q.length < 2) {
        this.clearSuggestions();
        return;
      }
      this.suggestOpen = true;
      this.suggestTimer = setTimeout(() => this.fetchSuggestions(q), 250);
    },
    async fetchSuggestions(q) {
      const requestId = ++this.suggestRequestId;
      this.suggesting = true;
      this.suggestTouched = true;
      try {
        const results = await ProductsServices.suggestProducts(q, 12);
        if (requestId !== this.suggestRequestId) return;
        const list = Array.isArray(results) ? results : results?.data || [];
        this.suggestions = list;
        this.activeIndex = list.length ? 0 : -1;
      } catch (e) {
        if (requestId !== this.suggestRequestId) return;
        this.suggestions = [];
        this.activeIndex = -1;
      } finally {
        if (requestId === this.suggestRequestId) this.suggesting = false;
      }
    },
    onSearchKeydown(event) {
      if (event.key === "ArrowDown") {
        if (!this.suggestions.length) return;
        event.preventDefault();
        this.suggestOpen = true;
        this.activeIndex = (this.activeIndex + 1) % this.suggestions.length;
        return;
      }
      if (event.key === "ArrowUp") {
        if (!this.suggestions.length) return;
        event.preventDefault();
        this.suggestOpen = true;
        this.activeIndex = this.activeIndex <= 0 ? this.suggestions.length - 1 : this.activeIndex - 1;
        return;
      }
      if (event.key === "Escape") {
        event.preventDefault();
        this.clearSuggestions();
        return;
      }
      if (event.key === "Enter") {
        event.preventDefault();
        if (this.suggestOpen && this.activeIndex >= 0 && this.suggestions[this.activeIndex]) {
          this.selectSuggestion(this.suggestions[this.activeIndex]);
          return;
        }
        this.clearSuggestions();
        this.$emit("pin-search");
      }
    },
    onSearchFocus() {
      if (this.suggestions.length || this.suggestTouched) this.suggestOpen = true;
    },
    onSearchBlur() {
      setTimeout(() => {
        this.suggestOpen = false;
      }, 150);
    },
    selectSuggestion(item) {
      if (!item?.code) return;
      this.clearSuggestions();
      this.$emit("select-suggestion", item);
    },
    clearSearch() {
      this.clearSuggestions();
      this.$emit("update:search", "");
    },
    clearSuggestions() {
      this.suggestRequestId += 1;
      if (this.suggestTimer) {
        clearTimeout(this.suggestTimer);
        this.suggestTimer = null;
      }
      this.suggestions = [];
      this.suggesting = false;
      this.suggestTouched = false;
      this.suggestOpen = false;
      this.activeIndex = -1;
    },
    formatFilterValue(field, value) {
      if (Array.isArray(value)) return value.filter(Boolean).join(", ");
      if (field === "status") return value === "1" ? "Aktiv" : value === "0" ? "Jo aktiv" : value;
      if (field === "status_filter") {
        const map = {
          active: "Aktiv",
          inactive: "Jo aktiv",
        };
        return map[value] || value;
      }
      if (field === "is_accessory") return value === "1" ? "Aksesor" : value === "0" ? "Produkt" : value;
      if (field === "has_offer" || field === "on_offer") return value === "1" ? "Po" : value === "0" ? "Jo" : value;
      if (field === "has_stock") return value === "1" ? "Me stok" : value === "0" ? "Pa stok" : value;
      if (field === "demo_version") return value === "1" ? "Po" : value === "0" ? "Jo" : value;
      if (field === "warranty_airbag") return value === "1" ? "Po" : value === "0" ? "Jo" : value;
      if (field === "in_web") return value === "1" ? "Po" : value === "0" ? "Jo" : value;
      if (field === "in_store") return value === "1" ? "Po" : value === "0" ? "Jo" : value;
      if (field === "delisted") return value === "1" ? "Delistuar" : value === "0" ? "Jo delistuar" : value;
      return value;
    },
    formatChipDate(value) {
      if (!value) return "";
      const parts = String(value).slice(0, 10).split("-");
      if (parts.length !== 3) return value;
      return `${parts[2]}/${parts[1]}/${parts[0]}`;
    },
    toggleOfferFilter() {
      this.$emit("toggle-offer", this.filters.on_offer !== "1");
    },
    toggleNewCodesFilter() {
      this.$emit("toggle-new-codes", this.filters.new_codes !== "1");
    },
    toggleEditedCodesFilter() {
      this.$emit("toggle-edited-codes", this.filters.edited_codes !== "1");
    },
    onDateRangeChange(field, value) {
      this.$emit("date-range", { field, value });
    },
  },
};
</script>

<style scoped>
.product-grid-toolbar {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
}

.toolbar-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem;
}

.toolbar-row--primary {
  gap: 0.75rem;
}

.toolbar-title {
  font-size: 1rem;
  font-weight: 600;
  margin: 0;
  white-space: nowrap;
}

.global-search {
  position: relative;
  flex: 1;
  min-width: 220px;
  max-width: 520px;
}

.global-search__icon {
  position: absolute;
  left: 0.65rem;
  top: 50%;
  transform: translateY(-50%);
  color: #9ca3af;
  pointer-events: none;
}

.global-search__input {
  padding-left: 2rem;
  padding-right: 2rem;
  width: 100%;
}

.global-search__clear {
  position: absolute;
  right: 0.35rem;
  top: 50%;
  transform: translateY(-50%);
  border: none;
  background: transparent;
  color: #6b7280;
  cursor: pointer;
  padding: 0.25rem 0.4rem;
}

.global-search__suggest {
  position: absolute;
  left: 0;
  right: 0;
  top: calc(100% + 2px);
  z-index: 30;
  margin: 0;
  padding: 0.25rem 0;
  list-style: none;
  background: #fff;
  border: 1px solid #dbeafe;
  border-radius: 6px;
  box-shadow: 0 8px 20px rgba(15, 23, 42, 0.12);
  max-height: 280px;
  overflow-y: auto;
}

.global-search__suggest-item {
  display: flex;
  align-items: baseline;
  gap: 0.5rem;
  padding: 0.4rem 0.75rem;
  cursor: pointer;
  font-size: 0.85rem;
}

.global-search__suggest-item--active,
.global-search__suggest-item:hover {
  background: #eff6ff;
}

.global-search__suggest-code {
  font-weight: 700;
  color: #1d4ed8;
  white-space: nowrap;
}

.global-search__suggest-name {
  color: #334155;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.global-search__suggest-note {
  margin-left: auto;
  font-size: 0.7rem;
  color: #94a3b8;
  white-space: nowrap;
}

.global-search__suggest-status {
  padding: 0.45rem 0.75rem;
  font-size: 0.8rem;
  color: #64748b;
}

.toolbar-loading {
  color: #6b7280;
  font-size: 0.85rem;
  white-space: nowrap;
}

.quick-filters {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.35rem;
}

.quick-filters__label {
  font-size: 0.8rem;
  color: #6b7280;
  margin-right: 0.15rem;
}

.chip {
  border: 1px solid #d1d5db;
  background: #fff;
  border-radius: 999px;
  padding: 0.15rem 0.65rem;
  font-size: 0.75rem;
  cursor: pointer;
}

.chip--active {
  background: #dbeafe;
  border-color: #3b82f6;
  color: #1d4ed8;
}

.date-range {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  font-size: 0.75rem;
  color: #6b7280;
}

.date-range__input {
  width: 9.5rem;
  height: 1.75rem;
  padding: 0.15rem 0.35rem;
  font-size: 0.75rem;
}

.toolbar-spacer {
  flex: 1;
}

.page-size {
  display: flex;
  align-items: center;
  gap: 0.35rem;
  font-size: 0.8rem;
  color: #6b7280;
}

.page-size select {
  border: 1px solid #d1d5db;
  border-radius: 4px;
  padding: 0.15rem 0.35rem;
  font-size: 0.8rem;
}

.toolbar-actions {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.toolbar-action {
  padding: 0.15rem 0.35rem;
  border-right: 1px solid #e5e7eb;
}

.toolbar-action--import {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  background: transparent;
  border: none;
  border-right: 1px solid #e5e7eb;
  color: #0f766e;
  cursor: pointer;
  font: inherit;
}

.toolbar-action--import:hover:not(:disabled) {
  color: #115e59;
}

.toolbar-action--import:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.toolbar-action--create {
  border-right: none;
  font-size: 1.1rem;
}

.filter-chip {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  border-radius: 999px;
  padding: 0.15rem 0.55rem;
  font-size: 0.75rem;
  color: #1e40af;
}

.filter-chip__remove {
  border: none;
  background: transparent;
  cursor: pointer;
  color: #3b82f6;
  padding: 0;
}

.filter-chip--clear {
  cursor: pointer;
  background: #f3f4f6;
  border-color: #d1d5db;
  color: #374151;
}

.result-summary {
  color: #6b7280;
}
</style>
